import { action, computed, observable } from "mobx";
import { observer, Observer } from "mobx-react";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import React, { useContext, useEffect, useMemo } from "react";
import { QueryClient, useQueryClient } from "react-query";
import styled from "styled-components";
import { candidateAPI } from "../../../api/candidate";
import { GetDetailsForCandidateResponse } from "../../../api/candidate/getDetailsForCandidate";
import { Type } from "../../../api/types/getTypes";
import { useCustomAlignDropdownOverlay } from "../../../Components/useCustomAlignOverlay";
import { getLocStore, useLocStore } from "../../../wsLocalization";
import { LocContext } from "../../../wsLocalization/LocContext";
import { Store2 } from "../../../WSUtils/Store";
import {
  invalidateQueries,
  useLatestQuery,
} from "../../../WSUtils/useLatestQuery";
import { UserInfo } from "../../../WSUtils/UserInfo";
import { UserInfoContext } from "../../../WSUtils/UserInfoContext";
import { useStore2 } from "../../../WSUtils/useStore";
import { WSFormGrid } from "../../../WSUtils/WSFormGrid";
import { sentryAssertData } from "../../../WSUtils/wsSentry";
import { CVEditContext, CVEditContextStore } from "../CVEditContext";
import {
  CVEditBaseField,
  cveField,
  cveFieldStore,
  OnRenderCvEditComponent,
} from "../Fields/CVEditBaseField";
import { defaultCvEditDateFieldDef } from "../Fields/defaultCvEditDateField";
import { defaultCvEditTextFieldDef } from "../Fields/defaultCvEditTextField";

export const CVEditPersonaliaSegment = () => {
  const L = useLocStore(LocContext.CV);
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);

  const store = useStore2(
    "CVEditPersonaliaSegmentStore",
    (dt) =>
      new CVEditPersonaliaSegmentStore({
        name: "CVEditPersonaliaSegmentStore",
        parentDt: dt,
        useHooks: (t: CVEditPersonaliaSegmentStore) => {
          t.userInfo = useContext(UserInfoContext);
          t.queryClient = useQueryClient();
          t.cvEditContext = useContext(CVEditContext);
        },
      })
  );

  const { data: details } = useLatestQuery(
    ["candidateAPI.getDetailsForCandidate"],
    () =>
      candidateAPI.getDetailsForCandidate({
        userInfo,
      })
  );

  sentryAssertData([details], { userInfo });

  useEffect(
    action(() => {
      store.details = details;
    }),
    [details]
  );

  return (
    <Observer
      render={() => (
        <CVEditPersonaliaSegmentStyles className="cve-personalia-segment cve-segment">
          <div className="cvp-h2 cve-header">
            {L.id("0541daf45795").loc("Personalia")}
          </div>

          <WSFormGrid horizontal>
            <CVEditBaseField
              def={store.firstNameField}
              containerProps={{ style: { flexGrow: 1 } }}
            />
            <CVEditBaseField
              def={store.lastNameField}
              containerProps={{ style: { flexGrow: 1 } }}
            />
          </WSFormGrid>

          <WSFormGrid horizontal>
            <CVEditBaseField
              def={store.birthDateField}
              containerProps={{ style: { flexGrow: 1 } }}
            />
            <CVEditBaseField
              def={store.genderField}
              containerProps={{ style: { flexGrow: 1 } }}
            />
          </WSFormGrid>
          <CVEditBaseField def={store.nationalityField} />
        </CVEditPersonaliaSegmentStyles>
      )}
    />
  );
};

const CVEditPersonaliaSegmentStyles = styled(WSFormGrid)``;

export class CVEditPersonaliaSegmentStore extends Store2<any> {
  declare userInfo: UserInfo;
  declare queryClient: QueryClient;
  declare cvEditContext: CVEditContextStore;

  @observable details: GetDetailsForCandidateResponse;

  getFieldDependencies = () => ({
    cvEditContext: this.cvEditContext,
    queryClient: this.queryClient,
    userInfo: this.userInfo,
    dt: this.dt,
  });

  @computed get L() {
    return getLocStore(this.userInfo, LocContext.CV);
  }

  @observable nationalityField = cveField((getValueVersioner) => {
    const _this = this;
    const store = cveFieldStore({
      value: 0,
      get validationError(): string {
        return "";
      },
    });

    const valueVersioner = getValueVersioner(
      store,
      0,
      true,
      "nationalityField"
    );

    this.dt.disposers.push(
      valueVersioner.onNewValue(() => {
        const data = this.details;
        if (!data?.ok) return;

        return {
          valueVersion: data?.valueVersion,
          getVal: () => data.typeNasjonalitet,
        };
      })
    );

    const onChange: DropdownProps["onChange"] = action((evt) => {
      const newValue: number = evt.value;

      const mutate = () =>
        this.userInfo.fullAPI.candidateAPI.updateCandidate({
          showSentryDialogOnError: true,
          userInfo: this.userInfo,
          fields: [
            {
              fieldName: "nasjonalitet",
              fieldValue: newValue.toString(),
            },
          ],
        });

      const invalidate = () =>
        invalidateQueries(_this.queryClient, [
          "candidateAPI.getDetailsForCandidate",
        ]);

      return valueVersioner.onChange({
        newValue,
        mutateBackend: mutate,
        invalidateQuery: invalidate,
      });
    });

    return {
      store,
      id: "Nationality",
      getLabel: () => this.L.id("502808d530ee").loc("Nasjonalitet"),
      hasUnsavedChanges: () => false,
      onRenderComponent: observer<OnRenderCvEditComponent>(({ def }) => {
        const userInfo = useContext(UserInfoContext);

        const { data: options } = useLatestQuery(
          ["typesAPI.getTypes(nasjonalitet)"],
          () =>
            userInfo.fullAPI.typesAPI.getTypes({
              userInfo: this.userInfo,
              group: "nasjonalitet",
            })
        );

        sentryAssertData(options, { userInfo });

        const ref = useCustomAlignDropdownOverlay();

        return (
          <Dropdown
            inputId={def.id}
            ref={ref}
            options={options?.mapped}
            value={store.value}
            onChange={onChange}
          />
        );
      }),
    };
  });

  @observable genderField = cveField((getValueVersioner) => {
    const _this = this;
    const store = cveFieldStore({
      value: 0,
      get validationError(): string {
        return "";
      },
    });

    const valueVersioner = getValueVersioner(store, 0, true, "genderField");

    this.dt.disposers.push(
      valueVersioner.onNewValue(() => {
        const data = this.details;
        if (!data?.ok) return;

        return {
          valueVersion: data?.valueVersion,
          getVal: () => data.typeKjonn,
        };
      })
    );

    const onChange: DropdownProps["onChange"] = action((evt) => {
      const newValue: number = evt.value;

      const mutate = () =>
        this.userInfo.fullAPI.candidateAPI.updateCandidate({
          showSentryDialogOnError: true,
          userInfo: this.userInfo,
          fields: [
            {
              fieldName: "kjonn",
              fieldValue: newValue.toString(),
            },
          ],
        });

      const invalidate = () =>
        invalidateQueries(_this.queryClient, [
          "candidateAPI.getDetailsForCandidate",
        ]);

      return valueVersioner.onChange({
        newValue,
        mutateBackend: mutate,
        invalidateQuery: invalidate,
      });
    });

    return {
      store,
      id: "Gender",
      getLabel: () => this.L.id("9175c775cbd9").loc("Kjønn"),
      hasUnsavedChanges: () => false,
      onRenderComponent: observer<OnRenderCvEditComponent>(({ def }) => {
        const userInfo = useContext(UserInfoContext);

        const { data: options } = useLatestQuery(
          ["typesAPI.getTypes(kjonn)"],
          () =>
            userInfo.fullAPI.typesAPI.getTypes({
              userInfo: this.userInfo,
              group: "kjonn",
            })
        );

        const optionsExtended = useMemo(() => {
          if (!options?.mapped) return [];

          return [
            ...options.mapped,
            { label: "- - - - - -", value: "0", key: "0" },
          ] as Type[];
        }, [options]);

        sentryAssertData(options, { userInfo });

        const ref = useCustomAlignDropdownOverlay();

        return (
          <Dropdown
            ref={ref}
            inputId={def.id}
            options={optionsExtended}
            value={store.value}
            onChange={onChange}
          />
        );
      }),
    };
  });

  @observable birthDateField = cveField((getValueVersioner) => {
    const def = defaultCvEditDateFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "fodselsDato",
      id: "BirthDate",
      label: "Fødselsdato",
      valueValidation: (value: Date) => {},
      ...this.getFieldDependencies(),
    });

    return def;
  });

  @observable firstNameField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "fornavn",
      id: "FirstName",
      label: "Fornavn",
      minLength: 1,
      maxLength: 50,
      onRenderComponent: (args) => (
        <Observer
          render={() => {
            const defaultProps =
              args.def.defaultGetOnRenderComponentProps(args);

            return <InputText {...defaultProps} disabled />;
          }}
        />
      ),
      ...this.getFieldDependencies(),
    });

    return def;
  });

  @observable lastNameField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "etternavn",
      id: "LastName",
      label: "Etternavn",
      minLength: 1,
      maxLength: 50,
      onRenderComponent: (args) => (
        <Observer
          render={() => {
            const defaultProps =
              args.def.defaultGetOnRenderComponentProps(args);

            return <InputText {...defaultProps} disabled />;
          }}
        />
      ),
      ...this.getFieldDependencies(),
    });

    return def;
  });
}
